import { useEffect, useLayoutEffect } from "react";
import type { NavigateOptions } from "react-router";
import { useStore } from "jotai";
import type { ToolboxApiRef } from "src/business-components/Toolbox/Toolbox.types";
import { useIsAiAssistantEnabled } from "src/business-hooks/useIsAiAssistantEnabled";
import {
  getCurrentSearchParams,
  useSearchParamState,
  useSearchParamValue,
  useSetSearchParam,
} from "src/hooks/useSearchParams";
import { toolboxConfigAtom } from "./AppToolboxSlot.atoms";
import {
  AI_TOOLBOX_SECTION_VALUE,
  DEFAULT_TOOLBOX_CONFIG,
  TOOLBOX_SEARCH_PARAM,
} from "./AppToolboxSlot.constants";
import type { ToolboxConfig } from "./AppToolboxSlot.types";

export const useToolboxConfig = (config: ToolboxConfig) => {
  const store = useStore();

  // Publish in a layout effect, not render: a render-time store.set notifies the
  // already-subscribed AppToolboxSlot mid-render ("Cannot update a component
  // while rendering a different component"). Layout effects run before paint and
  // React flushes the resulting slot update synchronously, so no empty frame.
  useLayoutEffect(() => {
    store.set(toolboxConfigAtom, config);
    return () => {
      // Only clear if a newer registration (same-page refresh, next route)
      // has not already replaced this one.
      if (store.get(toolboxConfigAtom) === config) {
        store.set(toolboxConfigAtom, DEFAULT_TOOLBOX_CONFIG);
      }
    };
  }, [config, store]);
};

export const useGetSelectedToolboxValue = () =>
  useSearchParamValue(TOOLBOX_SEARCH_PARAM);

export const useShowToolboxSection = () => {
  const setToolboxSection = useSetSearchParam(TOOLBOX_SEARCH_PARAM, {
    replace: false,
  });

  return (section: string, options?: NavigateOptions) => {
    if (!section) return;
    setToolboxSection(section, options);
  };
};

export const useAppToolboxState = (
  config: ToolboxConfig = DEFAULT_TOOLBOX_CONFIG,
  toolboxApiRef: ToolboxApiRef
) => {
  const { initialValue } = config;
  const [selectedToolboxSearchParamValue, setSelectedToolboxSearchParamValue] =
    useSearchParamState(TOOLBOX_SEARCH_PARAM, initialValue, { replace: true });

  const isAiEnabled = useIsAiAssistantEnabled();

  const apiRef = toolboxApiRef?.current ?? {};
  const lastSelected = apiRef.selectedSection;
  const deFactoSelected =
    lastSelected && !selectedToolboxSearchParamValue
      ? lastSelected
      : selectedToolboxSearchParamValue;

  const availableSections = config.sections.map((section) => section.value);
  const isValidSelectedToolbox =
    availableSections.includes(deFactoSelected) ||
    (deFactoSelected === AI_TOOLBOX_SECTION_VALUE && isAiEnabled);

  if (!isValidSelectedToolbox) {
    apiRef.selectedSection = undefined;
  }

  useEffect(() => {
    if (config.initialValue) {
      apiRef.selectedSection = config.initialValue;
    }
  }, [config.key]);

  // `config` not `initialValue`: many routes share the same default, and this
  // must re-run when the route's config object changes. Address bar, not the
  // hook value — React Router lags a redirect and would replace a deep link.
  useEffect(() => {
    if (
      !selectedToolboxSearchParamValue &&
      initialValue &&
      !getCurrentSearchParams().get(TOOLBOX_SEARCH_PARAM)
    ) {
      setSelectedToolboxSearchParamValue(initialValue);
    }
  }, [config]);

  return {
    selectedToolbox: isValidSelectedToolbox ? deFactoSelected : undefined,
    isAiEnabled,
    initialStorageValue: initialValue,
  };
};
